import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, categoryMap } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? categoryMap[slug] : undefined;
  const articles = slug ? getArticlesByCategory(slug) : [];

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-black text-foreground-950 mb-4">分类不存在</h1>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'CollectionPage',
              name: `${category.name} - 每日吃瓜`,
              description: category.description,
              url: `https://ryo7a.com/category/${category.slug}`,
              isPartOf: {
                '@type': 'WebSite',
                name: '每日吃瓜',
                url: 'https://ryo7a.com/',
              },
            }),
          }}
        />

        <section className="relative min-h-[420px] md:min-h-[500px] flex items-center overflow-hidden">
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(135deg, oklch(0.97 0.015 85) 0%, oklch(0.94 0.06 60) 40%, oklch(0.88 0.08 40) 100%)',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/20" />

          <div className="relative z-10 w-full px-4 md:px-6 pt-24 pb-10">
            <div className="max-w-[1280px] mx-auto">
              <div className="mb-6">
                <Breadcrumb
                  items={[
                    { label: '首页', path: '/' },
                    { label: category.name },
                  ]}
                />
              </div>

              <div className="flex flex-col lg:flex-row items-start gap-8">
                <div className="lg:w-[58%] flex flex-col gap-5">
                  <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-background-50/80 backdrop-blur-sm rounded-full text-xs font-medium text-primary-600 whitespace-nowrap">
                    <i className={`${category.icon}`} />
                    {category.name}
                  </span>
                  <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-black text-foreground-950 leading-tight">
                    {category.name}
                    <br />
                    最新爆料
                  </h1>
                </div>
                <div className="lg:w-[42%] flex flex-col gap-4 pt-4 lg:pt-16">
                  <p className="text-base text-foreground-700 leading-relaxed">
                    {category.description}。每日更新不打烊，吃瓜群众聚集地。
                  </p>
                  <p className="text-sm text-foreground-500">
                    共 {articles.length} 篇相关报道
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 px-4 md:px-6 pb-6 z-10">
            <div className="max-w-[1280px] mx-auto">
              <div className="w-full h-[150px] md:h-[200px] rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Chinese%20entertainment%20news%20collage%20with%20celebrities%2C%20dramatic%20warm%20lighting%2C%20editorial%20magazine%20style%2C%20low%20saturation%2C%20cinematic%20mood%2C%20dark%20atmosphere%2C%20mysterious%20vibe&width=1600&height=400&seq=chigua-category-hero&orientation=landscape"
                  alt={`${category.name} - 每日吃瓜`}
                  title={`${category.name} - 每日吃瓜`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {articles.length > 0 && (
          <section className="py-14 md:py-20 bg-background-50">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-[1280px] mx-auto">

                <div className="flex gap-2 overflow-x-auto pb-3 mb-10 scrollbar-hide">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                        cat.slug === slug
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200'
                      }`}
                    >
                      <i className={`${cat.icon} text-sm`} />
                      {cat.name}
                    </Link>
                  ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {articles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-background-300/60 transition-colors cursor-pointer flex flex-col"
                    >
                      <div className="relative h-[220px] md:h-[250px] overflow-hidden">
                        <img
                          src={article.imageUrl}
                          alt={article.imageAlt}
                          title={article.imageAlt}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        {article.isExclusive && (
                          <span className="absolute top-3 left-3 px-2.5 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded whitespace-nowrap">
                            独家爆料
                          </span>
                        )}
                      </div>
                      <div className="p-5 flex flex-col gap-3 flex-1">
                        <span className="inline-flex self-start px-3 py-1 bg-accent-100 text-accent-800 text-xs font-medium rounded-full whitespace-nowrap">
                          {category.name}
                        </span>
                        <h3 className="text-base md:text-lg font-semibold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-500 transition-colors">
                          {article.title}
                        </h3>
                        <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">
                          {article.summary}
                        </p>
                        <div className="mt-auto pt-3 flex items-center justify-between text-xs text-foreground-400">
                          <span>{article.author}</span>
                          <span className="flex items-center gap-4">
                            <span className="flex items-center gap-1">
                              <i className="ri-eye-line" />
                              {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
                            </span>
                            <span className="flex items-center gap-1">
                              <i className="ri-chat-3-line" />
                              {article.comments}
                            </span>
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-[1280px] mx-auto">
              <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-black text-foreground-950">
                    探索更多<span className="text-primary-500">分类</span>
                  </h2>
                  <p className="text-sm text-foreground-500 mt-2">更多精彩内容等你来挖</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className={`flex flex-col items-center gap-3 p-6 rounded-xl transition-colors cursor-pointer ${
                      cat.slug === slug
                        ? 'bg-primary-100/70 border border-primary-200'
                        : 'bg-background-50 border border-background-200 hover:border-background-300/60'
                    }`}
                  >
                    <span className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100">
                      <i className={`${cat.icon} text-xl text-accent-700`} />
                    </span>
                    <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">{cat.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
